<template>
  <div class="extraFilters">
    <div class="extraFilters-filterWrapper"
      v-if="!isDefault('customProperties')"
    >
      <neb-form-control>
        <neb-multi-searchable-select id="extraFilters-customProperties"
          name="extraFilters-customProperties"
          :value="customProperties"
          @input="value => customProperties = value"
          :options="propertyOptions"
          placeholder="Select property"
          :divider="true"
          fitToContainer
        />
      </neb-form-control>
    </div>

    <div class="extraFilters-filterWrapper"
      v-show="!isDefault('topics')"
    >
      <neb-form-control>
        <neb-multi-searchable-nested-select id="extraFilters-topics"
          name="extraFilters-topics"
          :value="topics"
          @input="value => topics = value"
          :options="topicOptions"
          placeholder="Select topic"
          fitToContainer
        />
      </neb-form-control>
    </div>

    <div class="extraFilters-filterWrapper"
      v-show="!isDefault('socialActions')"
    >
      <neb-form-control>
        <neb-simple-range-select v-model="socialActions"
          placeholder="All"
          prefix="Social Actions"
          highlightDirty
          clearable
          :defaultValue="[]"
          :loading="filterMetricsLoading.socialActions"
          showMax
          showAvg
          :max="filterMetrics.socialActions.MAX__SOCIAL_ACTIONS"
          :avg="filterMetrics.socialActions.AVG__SOCIAL_ACTIONS"
          valueType="int"
          @action:openDropdown="onOpenRangeDropdown('socialActions')"
        />
      </neb-form-control>
    </div>

    <div class="extraFilters-filterWrapper"
      v-show="!isDefault('wordCount')"
    >
      <neb-form-control>
        <neb-simple-range-select v-model="wordCount"
          placeholder="All"
          prefix="Word Count"
          highlightDirty
          clearable
          :defaultValue="[]"
          :loading="filterMetricsLoading.wordCount"
          showMax
          showAvg
          :max="filterMetrics.wordCount.MAX__WORD_COUNT"
          :avg="filterMetrics.wordCount.AVG__WORD_COUNT"
          valueType="int"
          @action:openDropdown="onOpenRangeDropdown('wordCount')"
        />
      </neb-form-control>
    </div>

    <div class="extraFilters-filterWrapper"
      v-show="!isDefault('timeToConsume')"
    >
      <neb-form-control>
        <neb-simple-range-select v-model="timeToConsume"
          placeholder="All"
          prefix="Time to Consume"
          highlightDirty
          clearable
          showMax
          showAvg
          :defaultValue="[]"
          :loading="filterMetricsLoading.duration"
          :max="filterMetrics.duration.MAX__DURATION"
          :avg="filterMetrics.duration.AVG__DURATION"
          valueType="seconds"
          @action:openDropdown="onOpenRangeDropdown('duration')"
        />
      </neb-form-control>
    </div>

    <div class="extraFilters-filterWrapper"
      v-show="!isDefault('language')"
    >
      <neb-form-control>
        <neb-select id="extraFilters-language"
          name="extraFilters-language"
          v-model="language"
          :options="languageOptions"
          placeholder="Select language"
          prefix="Language"
          highlightDirty
          :defaultValue="''"
          clearable
          @clear="language = ''"
        ></neb-select>
      </neb-form-control>
    </div>
  </div>
</template>

<script>
import { includes, isEqual, isEmpty } from 'lodash'

export default {
  name: 'ExtraFilters',

  props: {
    filters: {
      type: Object,
      default: () => ({})
    },
    propertyOptions: {
      type: Array,
      default: () => []
    },
    topicOptions: {
      type: Array,
      default: () => []
    },
    languageOptions: {
      type: Array,
      default: () => []
    }
  },

  computed: {
    filterMetricsLoading () { return this.$store.state.measure.filterMetricsLoading },
    filterMetrics () { return this.$store.state.measure.filterMetrics },
    brandIds: {
      get () { return this.filters.brandIds },
      set (value) { this.$emit('change', { key: 'brandIds', value }) }
    },
    socialActions: {
      get () { return this.filters.socialActions },
      set (value) { this.$emit('change', { key: 'socialActions', value }) }
    },
    wordCount: {
      get () { return this.filters.wordCount },
      set (value) { this.$emit('change', { key: 'wordCount', value }) }
    },
    timeToConsume: {
      get () { return this.filters.timeToConsume },
      set (value) { this.$emit('change', { key: 'timeToConsume', value }) }
    },
    language: {
      get () { return this.filters.language },
      set (value) { this.$emit('change', { key: 'language', value }) }
    },
    customProperties: {
      get () { return this.filters.customProperties },
      set (value) { this.$emit('change', { key: 'customProperties', value }) }
    },
    topics: {
      get () { return this.filters.topics },
      set (value) { this.$emit('change', { key: 'topics', value }) }
    },
    sponsoredContentEnabled () {
      if (this.filters.brandIds.length === 1 && this.filters.brandIds[0] === String(this.brandId)) {
        return true
      }

      const { competitors } = this.$store.state.brand.settings

      const allSelectedCompetitors = competitors
        .filter(obj => includes(this.filters.brandIds, obj.id))

      return allSelectedCompetitors.every(obj => obj.enableSponsoredContent)
    }
  },

  methods: {
    async onOpenRangeDropdown (metric) {
      if (isEmpty(this.filterMetrics[metric])) {
        await this.$store.dispatch('measure/loadFilterMetrics', { metric, brandId: this.brandId })
      }
    },
    isDefault (filterKey) {
      if (filterKey === 'brandIds') {
        return isEqual(this.filters.brandIds, [ String(this.brandId) ])
      }

      return isEqual(this.filters[filterKey], this.$store.getters['measure/defaultFilter'](filterKey))
    }
  }
}
</script>

<style lang="scss" scoped>
.extraFilters {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;

  &-filterWrapper {
    padding-bottom: 1em;
    box-sizing: border-box;
    flex-grow: 1;
    max-width: 25%;

    &:not(:last-child) {
      padding-right: 10px;
    }
  }
}
</style>

<style lang="scss">
.extraFilters {
  .nebFormControl {
    margin-bottom: 0 !important;

    .nebFormControl-content-wrapper {
      span:not(.el-checkbox-button__inner) {
        width: 100% !important;
      }
    }
  }
}
</style>
